import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Anchor, Breadcrumbs, Button, Group, Paper, ScrollArea, Stack, Table, Text } from '@mantine/core'

type FileEntry = {
  name: string
  type: 'file' | 'directory'
  size: number | null
  mtime: string
}

type FileListResponse = {
  path: string
  entries: FileEntry[]
}

const ROOT_PATH = '/'

function normalizePath(path: string): string {
  const trimmed = path.trim()
  if (!trimmed || trimmed === '/') return ROOT_PATH
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
}

function joinPath(basePath: string, name: string): string {
  const base = normalizePath(basePath)
  if (base === ROOT_PATH) return `/${name}`
  return `${base}/${name}`
}

function formatBytes(size: number | null): string {
  if (size === null) return '-'
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

function buildBreadcrumbs(path: string): Array<{ label: string; path: string }> {
  const normalized = normalizePath(path)
  if (normalized === ROOT_PATH) {
    return [{ label: 'root', path: ROOT_PATH }]
  }

  const segments = normalized.split('/').filter(Boolean)
  const crumbs: Array<{ label: string; path: string }> = [{ label: 'root', path: ROOT_PATH }]
  let current = ''
  for (const segment of segments) {
    current = `${current}/${segment}`
    crumbs.push({ label: segment, path: current })
  }

  return crumbs
}

export default function FileBrowser({ projectId, csrfToken }: { projectId: string; csrfToken?: string }) {
  const [currentPath, setCurrentPath] = useState<string>(ROOT_PATH)
  const [entries, setEntries] = useState<FileEntry[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [uploading, setUploading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [dragActive, setDragActive] = useState<boolean>(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const breadcrumbs = useMemo(() => buildBreadcrumbs(currentPath), [currentPath])

  const fetchDirectory = useCallback(async (path: string) => {
    const normalizedPath = normalizePath(path)
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`/api/v1/projects/${projectId}/files?path=${encodeURIComponent(normalizedPath)}`, {
        headers: { Accept: 'application/json' },
      })
      const payload = (await response.json()) as FileListResponse & { error?: string }

      if (!response.ok) {
        throw new Error(payload.error || 'Failed to load files')
      }

      setEntries(Array.isArray(payload.entries) ? payload.entries : [])
      setCurrentPath(normalizePath(payload.path || normalizedPath))
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load files'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    void fetchDirectory(ROOT_PATH)
  }, [fetchDirectory])

  const uploadFiles = useCallback(async (files: FileList | File[]) => {
    const list = Array.from(files)
    if (list.length === 0) return

    setUploading(true)
    setError(null)

    try {
      const formData = new FormData()
      list.forEach((file) => formData.append('files[]', file))

      const response = await fetch(`/api/v1/projects/${projectId}/files?path=${encodeURIComponent(currentPath)}`, {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': csrfToken || '',
        },
      })

      const payload = (await response.json()) as { error?: string }
      if (!response.ok) {
        throw new Error(payload.error || 'Failed to upload files')
      }

      await fetchDirectory(currentPath)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to upload files'
      setError(message)
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }, [csrfToken, currentPath, fetchDirectory, projectId])

  const deletePath = useCallback(async (path: string) => {
    const confirmed = window.confirm(`Delete ${path}?`)
    if (!confirmed) return

    setError(null)

    try {
      const response = await fetch(`/api/v1/projects/${projectId}/files?path=${encodeURIComponent(path)}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': csrfToken || '',
        },
      })

      if (!response.ok) {
        const payload = (await response.json()) as { error?: string }
        throw new Error(payload.error || 'Failed to delete path')
      }

      await fetchDirectory(currentPath)
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to delete path'
      setError(message)
    }
  }, [csrfToken, currentPath, fetchDirectory, projectId])

  return (
    <Paper p="lg">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Breadcrumbs separator="/">
            {breadcrumbs.map((crumb) => (
              <Anchor key={crumb.path} component="button" type="button" onClick={() => void fetchDirectory(crumb.path)}>
                {crumb.label}
              </Anchor>
            ))}
          </Breadcrumbs>

          <div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={(event) => {
                if (event.target.files) {
                  void uploadFiles(event.target.files)
                }
              }}
              hidden
            />
            <Button type="button" variant="light" onClick={() => fileInputRef.current?.click()} loading={uploading}>
              Upload Files
            </Button>
          </div>
        </Group>

        <Paper
          withBorder
          p="md"
          onDragOver={(event) => {
            event.preventDefault()
            setDragActive(true)
          }}
          onDragLeave={(event) => {
            event.preventDefault()
            setDragActive(false)
          }}
          onDrop={(event) => {
            event.preventDefault()
            setDragActive(false)
            if (event.dataTransfer.files) {
              void uploadFiles(event.dataTransfer.files)
            }
          }}
          style={(theme) => ({
            borderStyle: 'dashed',
            borderColor: dragActive ? theme.colors.teal[4] : theme.colors.gray[4],
            backgroundColor: dragActive ? theme.colors.teal[0] : theme.white,
          })}
        >
          <Text ta="center" c="dimmed" size="sm">
            Drag and drop files here
          </Text>
        </Paper>

        {error ? <Text c="red" size="sm">{error}</Text> : null}

        <ScrollArea>
          <Table striped highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Size</Table.Th>
                <Table.Th>Modified</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text c="dimmed" size="sm">Loading...</Text>
                  </Table.Td>
                </Table.Tr>
              ) : entries.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text c="dimmed" size="sm">No files in this directory.</Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                entries.map((entry) => {
                  const absolutePath = joinPath(currentPath, entry.name)

                  return (
                    <Table.Tr key={absolutePath}>
                      <Table.Td>
                        {entry.type === 'directory' ? (
                          <Anchor component="button" type="button" onClick={() => void fetchDirectory(absolutePath)}>
                            {entry.name}/
                          </Anchor>
                        ) : (
                          <Text size="sm">{entry.name}</Text>
                        )}
                      </Table.Td>
                      <Table.Td><Text size="sm">{entry.type}</Text></Table.Td>
                      <Table.Td><Text size="sm">{formatBytes(entry.size)}</Text></Table.Td>
                      <Table.Td><Text size="sm">{new Date(entry.mtime).toLocaleString()}</Text></Table.Td>
                      <Table.Td>
                        <Group gap="sm">
                          {entry.type === 'file' ? (
                            <Anchor href={`/api/v1/projects/${projectId}/files/download?path=${encodeURIComponent(absolutePath)}`}>
                              Download
                            </Anchor>
                          ) : null}
                          <Anchor component="button" type="button" c="red" onClick={() => void deletePath(absolutePath)}>
                            Delete
                          </Anchor>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  )
                })
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Stack>
    </Paper>
  )
}
